import React, { Suspense, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';

import i18n from '../i18n';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';

const LOCALE_STORAGE_KEY = 'i18nextLng';

/**
 * I18nProvider wraps the app with i18next context.
 *
 * Responsibilities:
 * - Loads locale JSON files from /locales/{lng}/translation.json via i18next-http-backend
 * - On login, syncs the user's stored locale preference from their profile
 * - Persists locale changes to both localStorage (instant) and the API (durable)
 * - Re-renders in the selected language reactively (i18next handles this within 1 second)
 */
export function I18nProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // On login, load user's stored locale preference and apply it
  useEffect(() => {
    if (isAuthenticated && user) {
      const userLocale = user.locale;
      if (userLocale && i18n.language !== userLocale) {
        i18n.changeLanguage(userLocale);
        localStorage.setItem(LOCALE_STORAGE_KEY, userLocale);
      }
    }
  }, [isAuthenticated, user]);

  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<></>}>{children}</Suspense>
    </I18nextProvider>
  );
}

/**
 * Change the app language and persist the preference.
 * - Updates i18next language immediately (triggers re-render within 1 second)
 * - Saves to localStorage for persistence across page reloads
 * - If authenticated, saves to user profile via PATCH /users/me
 */
export async function changeLanguage(locale: string): Promise<void> {
  // Immediate switch — i18next will reactively re-render all translated components
  await i18n.changeLanguage(locale);
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);

  // Persist to backend if user is authenticated
  const { token } = useAuthStore.getState();
  if (token) {
    try {
      await api.patch('/users/me', { locale });
    } catch {
      // Silently fail — localStorage already has the value; API sync will happen next login
    }
  }
}

export { i18n };
